import {
  createNestablePublicClientApplication,
  PublicClientApplication,
  type IPublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
  type SilentRequest,
  type PopupRequest,
  type AccountInfo,
} from "@azure/msal-browser";

import { getApiScopes, getMsalConfig } from "../config/msalConfig";
import { detectAuthEnvironment, resetAuthEnvironmentCache, type AuthEnvironment } from "./environment";
import { AuthError, type AuthErrorCode } from "./types";

export { AuthError } from "./types";
export type { AuthStrategy, AuthErrorCode } from "./types";

export type GetTokenOptions = {
  forceRefresh?: boolean;
};

const EXPIRY_BUFFER_MS = 60_000;
const FALLBACK_TTL_MS = 5 * 60_000;

// Office.auth.getAccessToken error codes
// Ref: https://learn.microsoft.com/javascript/api/office-runtime/officeruntime.auth
const OFFICE_SSO_ERROR = {
  API_NOT_SUPPORTED: 13000,
  USER_NOT_SIGNED_IN: 13001,
  USER_CANCELLED: 13002,
  MSA_ACCOUNT: 13003,
  TOKEN_RETRIEVAL_FAILED: 13005,
  INVALID_RESOURCE: 13006,
  SERVICE_UNAVAILABLE: 13007,
  ADD_IN_ERROR: 13012,
  RESOURCE_NOT_FOUND: 50001,
} as const;

const MSAL_POPUP_ERROR_CODE = "popup_window_error";

type CachedToken = {
  token: string;
  expiresAt: number;
};

const state = {
  cached: null as CachedToken | null,
  inflight: null as Promise<string> | null,
  naa: null as IPublicClientApplication | null,
  browser: null as IPublicClientApplication | null,
};

function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(payloadB64)) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function cacheToken(token: string): void {
  state.cached = {
    token,
    expiresAt: parseJwtExp(token) ?? Date.now() + FALLBACK_TTL_MS,
  };
}

function readCache(): string | null {
  const entry = state.cached;
  if (!entry) return null;
  if (entry.expiresAt - EXPIRY_BUFFER_MS <= Date.now()) {
    state.cached = null;
    return null;
  }
  return entry.token;
}

async function getNaaInstance(): Promise<IPublicClientApplication> {
  if (!state.naa) {
    state.naa = await createNestablePublicClientApplication(getMsalConfig("naa"));
  }
  return state.naa;
}

async function getBrowserInstance(): Promise<IPublicClientApplication> {
  if (!state.browser) {
    const instance = new PublicClientApplication(getMsalConfig("browser"));
    await instance.initialize();
    await instance.handleRedirectPromise();
    state.browser = instance;
  }
  return state.browser;
}

function pickAccount(instance: IPublicClientApplication, loginHint?: string): AccountInfo | undefined {
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return undefined;
  if (loginHint) {
    const match = accounts.find(
      (a) => a.username && a.username.toLowerCase() === loginHint.toLowerCase()
    );
    if (match) return match;
  }
  return accounts[0];
}

function getLoginHint(): string | undefined {
  try {
    if (typeof Office !== "undefined") {
      return Office.context?.mailbox?.userProfile?.emailAddress;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

async function acquireSilent(
  instance: IPublicClientApplication,
  scopes: string[],
  account: AccountInfo,
  forceRefresh: boolean
): Promise<string | null> {
  const req: SilentRequest = { scopes, account, forceRefresh };
  try {
    const res = await instance.acquireTokenSilent(req);
    return res.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) return null;
    throw err;
  }
}

async function tryNaa(forceRefresh: boolean): Promise<string> {
  const instance = await getNaaInstance();
  const scopes = getApiScopes();
  const loginHint = getLoginHint();
  const account = pickAccount(instance, loginHint);

  if (account) {
    const token = await acquireSilent(instance, scopes, account, forceRefresh);
    if (token) return token;
  } else {
    try {
      const res = await instance.ssoSilent({ scopes, loginHint });
      return res.accessToken;
    } catch {
      // ssoSilent failed — no existing session.
    }
  }

  // NAA does not support acquireTokenPopup / acquireTokenRedirect; bail to the next strategy.
  throw new AuthError(
    "INTERACTION_REQUIRED",
    "NAA silent acquisition failed; interactive flow required.",
    "naa"
  );
}

async function tryMsalBrowser(forceRefresh: boolean): Promise<string> {
  const instance = await getBrowserInstance();
  const scopes = getApiScopes();
  const loginHint = getLoginHint();
  const account = pickAccount(instance, loginHint);

  if (account) {
    const token = await acquireSilent(instance, scopes, account, forceRefresh);
    if (token) return token;
  }

  // Task panes always run in an iframe — redirect is not usable, popup opens a top-level window.
  const req: PopupRequest = { scopes, loginHint, prompt: "select_account" };
  try {
    const res = await instance.acquireTokenPopup(req);
    return res.accessToken;
  } catch (err) {
    if (err instanceof BrowserAuthError && err.errorCode === MSAL_POPUP_ERROR_CODE) {
      throw new AuthError(
        "POPUP_BLOCKED",
        "Sign-in window was blocked. Please allow popups for this add-in and try again.",
        "msal-browser-popup",
        err
      );
    }
    throw err;
  }
}

function mapOfficeErrorCode(code: number): { authCode: AuthErrorCode; message: string } {
  switch (code) {
    case OFFICE_SSO_ERROR.API_NOT_SUPPORTED:
      return {
        authCode: "API_NOT_AVAILABLE",
        message: "Office identity API is not available in this host.",
      };
    case OFFICE_SSO_ERROR.USER_NOT_SIGNED_IN:
      return {
        authCode: "INTERACTION_REQUIRED",
        message: "Please sign in to your Microsoft account.",
      };
    case OFFICE_SSO_ERROR.USER_CANCELLED:
      return {
        authCode: "USER_CANCELLED",
        message: "Sign-in was cancelled.",
      };
    case OFFICE_SSO_ERROR.MSA_ACCOUNT:
      return {
        authCode: "ACCOUNT_NOT_SUPPORTED",
        message:
          "This account type is not supported. Please use a Microsoft 365 work or school account.",
      };
    case OFFICE_SSO_ERROR.TOKEN_RETRIEVAL_FAILED:
    case OFFICE_SSO_ERROR.INVALID_RESOURCE:
    case OFFICE_SSO_ERROR.SERVICE_UNAVAILABLE:
    case OFFICE_SSO_ERROR.ADD_IN_ERROR:
      return {
        authCode: "API_NOT_AVAILABLE",
        message: "Office sign-in is not available right now.",
      };
    case OFFICE_SSO_ERROR.RESOURCE_NOT_FOUND:
      return {
        authCode: "API_NOT_AVAILABLE",
        message: "Office identity resource is not available.",
      };
    default:
      return {
        authCode: "UNKNOWN",
        message: `Office SSO failed (code ${code}).`,
      };
  }
}

async function tryOfficeSso(): Promise<string> {
  if (typeof Office === "undefined" || !Office.auth?.getAccessToken) {
    throw new AuthError(
      "API_NOT_AVAILABLE",
      "Office.auth.getAccessToken is not available.",
      "office-sso"
    );
  }

  try {
    return await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: false,
    });
  } catch (rawError) {
    const err = rawError as { code?: number; name?: string; message?: string };
    const code = typeof err.code === "number" ? err.code : 0;
    const mapped = mapOfficeErrorCode(code);
    throw new AuthError(mapped.authCode, mapped.message, "office-sso", rawError);
  }
}

function isTerminal(err: unknown): boolean {
  return (
    err instanceof AuthError &&
    (err.code === "USER_CANCELLED" || err.code === "ACCOUNT_NOT_SUPPORTED")
  );
}

async function acquireWithCascade(
  env: AuthEnvironment,
  forceRefresh: boolean
): Promise<string> {
  if (env.naaSupported) {
    try {
      const token = await tryNaa(forceRefresh);
      cacheToken(token);
      return token;
    } catch (err) {
      console.warn("[AUTH] NAA strategy failed, falling back", err);
      if (isTerminal(err)) throw err;
    }
  }

  if (env.identityApiSupported || typeof Office !== "undefined") {
    try {
      const token = await tryOfficeSso();
      cacheToken(token);
      return token;
    } catch (err) {
      console.warn("[AUTH] Office SSO strategy failed, falling back", err);
      if (isTerminal(err)) throw err;
    }
  }

  try {
    const token = await tryMsalBrowser(forceRefresh);
    cacheToken(token);
    return token;
  } catch (err) {
    console.error("[AUTH] All strategies failed", err);
    if (err instanceof AuthError) throw err;
    const e = err as { message?: string };
    throw new AuthError(
      "ALL_STRATEGIES_FAILED",
      e.message || "Authentication failed across all available strategies.",
      "msal-browser-popup",
      err
    );
  }
}

export async function getDeskareAccessToken(
  options: GetTokenOptions = {}
): Promise<string> {
  const forceRefresh = Boolean(options.forceRefresh);

  if (!forceRefresh) {
    const cached = readCache();
    if (cached) return cached;
  } else {
    state.cached = null;
  }

  if (state.inflight) return state.inflight;

  const env = detectAuthEnvironment();
  state.inflight = acquireWithCascade(env, forceRefresh);
  try {
    return await state.inflight;
  } finally {
    state.inflight = null;
  }
}

export function clearAuthCache(): void {
  state.cached = null;
  state.inflight = null;
  // Host (Outlook) owns the NAA cache; dropping our reference forces a fresh handshake.
  state.naa = null;
  state.browser = null;
  resetAuthEnvironmentCache();
}

export async function signOut(): Promise<void> {
  const env = detectAuthEnvironment();
  try {
    if (!env.naaSupported && state.browser) {
      const accounts = state.browser.getAllAccounts();
      await state.browser.logoutPopup({
        account: accounts.length > 0 ? accounts[0] : undefined,
      });
    }
  } catch (err) {
    console.warn("[AUTH] Sign-out failed", err);
  } finally {
    clearAuthCache();
  }
}
